import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import type { DateRange } from '@mui/x-date-pickers-pro';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';

export interface DownloadDialogProps {
  close: () => void;
  exportUrl: string | null;
  onExport: (startDate?: DateTime, endDate?: DateTime) => Promise<void>;
  exporting: boolean;
  totalCount: number | undefined;
  minYear: number | null | undefined;
  maxYear: number | null | undefined;
}

export const DownloadDialog = ({
  close,
  exportUrl,
  onExport,
  exporting,
  totalCount,
  minYear,
  maxYear,
}: DownloadDialogProps) => {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState<DateRange<DateTime>>([null, null]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Generate year options based on min/max years
  const yearOptions = useMemo(() => {
    if (!minYear || !maxYear) return [];
    const years: number[] = [];
    for (let year = maxYear; year >= minYear; year--) {
      years.push(year);
    }
    return years;
  }, [minYear, maxYear]);

  // When year changes, update date range to match full year
  useEffect(() => {
    if (selectedYear && typeof selectedYear === 'number') {
      const startOfYear = DateTime.fromObject({ year: selectedYear, month: 1, day: 1 });
      const endOfYear = DateTime.fromObject({ year: selectedYear, month: 12, day: 31 }).endOf(
        'day',
      );
      setDateRange([startOfYear, endOfYear]);
    }
  }, [selectedYear]);

  const handleClearFilters = () => {
    setSelectedYear(null);
    setDateRange([null, null]);
    setError(null);
  };

  const handleExport = async () => {
    setError(null);
    try {
      await onExport(dateRange[0] ?? undefined, dateRange[1] ?? undefined);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t('pages.audit_logs.export_dialog.error_unexpected'));
      }
    }
  };

  const handleClose = () => {
    setSelectedYear(null);
    setDateRange([null, null]);
    setError(null);
    close();
  };

  // If we have an export URL, show the success dialog
  if (exportUrl) {
    return (
      <Dialog fullWidth maxWidth="md" onClose={handleClose} open={Boolean(exportUrl)}>
        <DialogTitle onClose={handleClose}>
          {t('pages.audit_logs.export_dialog.success_title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentTitle>
            {t('pages.audit_logs.export_dialog.success_message')}
          </DialogContentTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1">
              {t('pages.audit_logs.export_dialog.download_prompt')}
            </Typography>
            <Link sx={{ fontSize: '1.2em' }} target="_blank" rel="noreferrer" href={exportUrl}>
              {t('pages.audit_logs.export_dialog.download_link_text')}
            </Link>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            {t('common.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Otherwise show the export configuration dialog
  return (
    <Dialog fullWidth maxWidth="md" onClose={handleClose} open>
      <DialogTitle onClose={handleClose}>{t('pages.audit_logs.export_dialog.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <DialogContentTitle>
            {t('pages.audit_logs.export_dialog.configure_message')}
          </DialogContentTitle>

          {totalCount && totalCount > 250000 && (
            <Alert severity="warning">
              {t('pages.audit_logs.export_dialog.limit_warning', {
                count: totalCount,
              })}
            </Alert>
          )}

          {yearOptions.length > 0 && (
            <Box>
              <Typography component="h3" variant="subtitle2" sx={{ mb: 1 }}>
                {t('pages.audit_logs.export_dialog.year_section_title')}
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="year-select-label">
                  {t('pages.audit_logs.export_dialog.year_label')}
                </InputLabel>
                <Select
                  labelId="year-select-label"
                  value={selectedYear}
                  label={t('pages.audit_logs.export_dialog.year_label')}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                  <MenuItem value="">
                    <em>{t('common.labels.none')}</em>
                  </MenuItem>
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {t('pages.audit_logs.export_dialog.year_help')}
              </Typography>
            </Box>
          )}

          <Box>
            <Typography component="h3" variant="subtitle2" sx={{ mb: 1 }}>
              {t('pages.audit_logs.export_dialog.date_range_title')}
            </Typography>
            <DateRangePicker
              value={dateRange}
              onChange={(newValue) => {
                setDateRange(newValue);
                if (selectedYear) setSelectedYear(null);
              }}
              disabled={!!selectedYear}
              slotProps={{
                textField: { fullWidth: true },
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {selectedYear
                ? t('pages.audit_logs.export_dialog.date_range_help_with_year')
                : t('pages.audit_logs.export_dialog.date_range_help')}
            </Typography>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClearFilters} variant="text" disabled={exporting}>
          {t('pages.audit_logs.actions.clear_filters')}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleClose} variant="outlined" disabled={exporting}>
          {t('common.actions.cancel')}
        </Button>
        <Button onClick={handleExport} variant="contained" disabled={exporting}>
          {exporting ? t('common.buttons.exporting') : t('common.buttons.export')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
